import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Select, Spin, Avatar } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import { getClients } from '../../../services/clients';

const debounce = (fn, ms) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
};

const MessagePreview = ({ messageText, attachments = [] }) => {
  const auth = useSelector(state => state.auth);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searching, setSearching] = useState(false);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const searchRef = useRef(null);

  // Load first client on mount
  useEffect(() => {
    if (initialLoaded) return;
    let cancelled = false;
    const loadInitial = async () => {
      try {
        const res = await getClients({ data: { limit: 1, offset: 0, archived: false }, loadingStatusOff: true });
        const list = res?.data || [];
        if (!cancelled && list.length > 0) {
          setClients(list);
          setSelectedClient(list[0]);
        }
      } catch {
        // Ignore — preview still works without a client
      } finally {
        if (!cancelled) setInitialLoaded(true);
      }
    };
    loadInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSearch = useMemo(
    () =>
      debounce(async term => {
        if (!term || term.length < 2) return;
        setSearching(true);
        try {
          const res = await getClients({
            data: { searchTerm: term, limit: 20, offset: 0, archived: false },
            loadingStatusOff: true,
          });
          setClients(res?.data || []);
        } catch {
          // Ignore
        } finally {
          setSearching(false);
        }
      }, 350),
    [],
  );

  const handleSelect = useCallback(
    clientId => {
      const client = clients.find(c => c.id === clientId);
      if (client) setSelectedClient(client);
    },
    [clients],
  );

  // Build the substituted preview text
  const previewText = useMemo(() => {
    if (!messageText) return '';
    let text = messageText;

    const storeName = auth.stores?.find(s => s.id === auth.storeId)?.name || '';

    const substitutions = {
      ':name:': selectedClient?.name || 'Client',
      ':associate:': [auth.firstName, auth.lastName].filter(Boolean).join(' ') || 'Associate',
      ':assigned_associate_name:': [auth.firstName, auth.lastName].filter(Boolean).join(' ') || 'Associate',
      ':store:': storeName || 'Store',
      ':merchant:': auth.brandName || 'Merchant',
      ':review_link:': 'https://g.co/r/...',
      ':appointment_date:': 'Jan 15, 2026',
      ':appointment_time:': '2:00 PM',
    };

    Object.entries(substitutions).forEach(([token, value]) => {
      text = text.replace(new RegExp(token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
    });

    return text;
  }, [messageText, selectedClient, auth]);

  const clientOptions = useMemo(
    () =>
      clients.map(c => ({
        value: c.id,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {c.image ? (
              <Avatar size={20} src={c.image} />
            ) : (
              <Avatar size={20} style={{ backgroundColor: c.avatar || '#3aa4fc', fontSize: 10 }}>
                {(c.name || '?')[0].toUpperCase()}
              </Avatar>
            )}
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</span>
          </div>
        ),
      })),
    [clients],
  );

  return (
    <div className="mass-message-form__preview">
      <Select
        ref={searchRef}
        className="mass-message-form__preview-client-select"
        showSearch
        filterOption={false}
        placeholder="Preview as client..."
        value={selectedClient?.id}
        onSearch={handleSearch}
        onSelect={handleSelect}
        options={clientOptions}
        notFoundContent={searching ? <Spin size="small" /> : null}
        suffixIcon={<FontAwesomeIcon icon={['far', 'search']} style={{ color: '#bfbfbf' }} />}
        popupMatchSelectWidth={false}
        style={{ width: '100%' }}
        size="small"
      />

      <div className="mass-message-form__preview-body">
        <div className="mass-message-form__preview-bubble">
          <div className="mass-message-form__preview-bubble-text">
            {previewText || 'Your message will appear here...'}
          </div>
          {attachments.length > 0 && (
            <div className="mass-message-form__preview-attachments">
              {attachments.map((att, i) => {
                const url = typeof att === 'string' ? att : att.url;
                return url && !att.uploading ? (
                  <img key={att.id || i} src={url} alt="" className="mass-message-form__preview-att-img" />
                ) : null;
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessagePreview;
